import { SpinnerLoader } from "@/components/spinner-loader";
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { GetEnrolleeIdModal } from "@/features/hmo-consultation/components/get-enrollee-id-modal";
import { useHMOStore } from "@/features/hmo-consultation/hooks/use-hmo-store";
import { FormSchemaType } from "@/features/hmo-consultation/schema";
import {
  GetHMOUserResponse,
  THMOUser,
} from "@/features/hmo-consultation/types";
import { addHMOUserDataToFormState } from "@/features/hmo-consultation/utils";
import { useWidgetColor } from "@/hooks/useWidgetColor";
import { restApi } from "@/lib/rest-api-client";
import { useQuery } from "@tanstack/react-query";
import { CircleCheckBig } from "lucide-react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDebounceValue } from "usehooks-ts";

const externalSearchWhitelist = (
  import.meta.env.VITE_APP_EXTERNAL_SEARCH_WHITELIST || ""
).split(" ");

const hpID = (import.meta.env.VITE_APP_HPID || "").split(" ");

type EnrolleeIdProps = {
  setFoundUser: React.Dispatch<React.SetStateAction<boolean>>;
};

export function EnrolleeIdNoOwnerType(props: EnrolleeIdProps) {
  const appState = useHMOStore();
  const widgetColor = useWidgetColor();
  const { setFoundUser } = props;

  const name = "hmoId";

  const { providerId } = appState?.partnerInfo || {};
  const { control, watch, setValue, setError, clearErrors } =
    useFormContext<FormSchemaType>();

  const formHmoId = watch(name)?.trim();

  const isExternalSearch = externalSearchWhitelist.includes(providerId);

  const [debouncedFormHmoId] = useDebounceValue(formHmoId, 500);
  const [persistentError, setPersistentError] = useState<string | null>(null);

  const {
    data: hmoUserById,
    status,
    isLoading: isHMOUserLoading,
    error: hmoUserError,
  } = useQuery({
    queryKey: [name, debouncedFormHmoId],
    queryFn: async () =>
      isExternalSearch
        ? await GET_HMO_USER_EXTERNAL(providerId!, formHmoId!)
        : await GET_HMO_USER(providerId!, formHmoId!),
    enabled: !!formHmoId,
    select: (response) => {
      if (isExternalSearch) {
        return response?.data?.data as unknown as THMOUser;
      } else {
        return response?.data?.data?.[0] as THMOUser;
      }
    },
  });

  const hmoUserData = hmoUserById;
  const foundUserForId = !!hmoUserData && !!formHmoId;

  useEffect(() => {
    if (!hmoUserData) {
      clearErrors(name);
      setPersistentError(null);
      setFoundUser(false);
      return;
    }

    // Check if expiry date is invalid or expired
    const expiryDate = new Date(hmoUserData.expiryDate);
    const today = new Date();

    if (isNaN(expiryDate.getTime()) || expiryDate < today) {
      const errorMsg =
        "HMO plan has expired. Contact your provider for a new plan.";
      setPersistentError(errorMsg);
      setError(
        name,
        {
          type: "manual",
          message: "",
        },
        { shouldFocus: false },
      );
      setFoundUser(false);
      return;
    }

    if (hmoUserData?.deactivated) {
      const errorMsg = "Account deactivated! Contact your provider.";
      setPersistentError(errorMsg);
      setError(
        name,
        {
          type: "manual",
          message: "",
        },
        { shouldFocus: false },
      );
      setFoundUser(false);
      return;
    }

    if (hmoUserData?.allowTeleMedicine === false) {
      const errorMsg =
        "Telemedicine not allowed for this account! Contact your provider.";
      setPersistentError(errorMsg);
      setError(
        name,
        {
          type: "manual",
          message: "",
        },
        { shouldFocus: false },
      );
      setFoundUser(false);
      return;
    }

    // Set consultation owner and principal ID based on enrollee type returned from external search
    if (isExternalSearch) {
      if (hmoUserData.enrolleeType === "dependent") {
        setValue("consultationOwner", "Dependant");
        setValue("principalHmoId", hmoUserData.principalHmoId);
      } else {
        setValue("consultationOwner", "Myself");
        setValue("principalHmoId", undefined);
      }
    }

    clearErrors(name);
    setPersistentError(null);
    setFoundUser(foundUserForId);

    appState.setValues({
      userData: hmoUserData,
    });

    addHMOUserDataToFormState(setValue, hmoUserData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hmoUserData, foundUserForId]);

  const label = "HMO/Access ID";

  const placeHolder = "Enter your ID";

  const enrolleeInfo =
    "This is the HMO/Access ID given to you by your provider.";

  const [showRetrieveIDModal, setShowRetrieveIDModal] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel required className="">
            {label}
          </FormLabel>
          <FormDescription>{enrolleeInfo}</FormDescription>

          <div className="flex gap-2 items-center">
            <Input
              {...field}
              onChange={(e) => field.onChange(e.target.value.trim())}
              placeholder={placeHolder}
              className="my-[2px]"
            />
            {isHMOUserLoading && <SpinnerLoader />}
            {foundUserForId && !isHMOUserLoading && (
              <CircleCheckBig className="text-green-600" />
            )}
          </div>

          {!foundUserForId && !isHMOUserLoading && (
            <FormDescription>
              Can't remember your access id
              <span
                style={{ color: widgetColor }}
                className="cursor-pointer"
                onClick={() => setShowRetrieveIDModal(true)}
              >
                {" "}
                click here
              </span>
              {showRetrieveIDModal && (
                <GetEnrolleeIdModal
                  open={showRetrieveIDModal}
                  onClose={() => setShowRetrieveIDModal(false)}
                />
              )}
            </FormDescription>
          )}

          {!foundUserForId && hpID.includes(providerId) && (
            <FormDescription>
              Don't have an access id,
              <a
                href="https://onelink.to/wqu28p"
                target="_blank"
                className="cursor-pointer text-primary"
              >
                {" "}
                sign up.
              </a>
            </FormDescription>
          )}

          <>
            <FormMessage />

            {persistentError && (
              <p className="text-sm font-medium text-destructive">
                {persistentError}
              </p>
            )}

            {hmoUserError?.message && (
              <p className="text-sm font-medium text-destructive">
                {hmoUserError?.message}
              </p>
            )}

            {!foundUserForId &&
              !isHMOUserLoading &&
              status !== "pending" &&
              !persistentError && (
                <p className="text-sm font-medium text-destructive">
                  Invalid ID
                </p>
              )}
          </>
        </FormItem>
      )}
    />
  );
}

const GET_HMO_USER = async (providerId: string, hmoId: string) =>
  await restApi.get<GetHMOUserResponse>(
    `/enrollees?filterBy[providerId]=${encodeURIComponent(providerId)}&filterBy[hmoId]=${encodeURIComponent(hmoId.toUpperCase())}&page=1`,
  );

const GET_HMO_USER_EXTERNAL = async (providerId: string, hmoId: string) => {
  return await restApi.post<GetHMOUserResponse>(`/enrollees/search`, {
    providerId,
    hmoId: `${hmoId}`.toLowerCase(),
  });
};
